import { useState, useCallback, useRef } from 'react';
import { ApiService, ApiError } from '../services/api';

interface UseVoiceOptions {
  apiService: ApiService | null;
  onTranscription?: (text: string) => void;
}

interface UseVoiceReturn {
  isRecording: boolean;
  isProcessing: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  cancelRecording: () => void;
  error: string | null;
  resetError: () => void;
  isSupported: boolean;
}

// Check if MediaRecorder is supported
function isMediaRecorderSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
  if (!window.MediaRecorder) return false;

  // Check for WebM Opus support
  return MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ||
    MediaRecorder.isTypeSupported('audio/webm');
}

export function useVoice({
  apiService,
  onTranscription,
}: UseVoiceOptions): UseVoiceReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const isSupported = isMediaRecorderSupported();

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  }, []);

  const startRecording = useCallback(async (): Promise<void> => {
    if (!isSupported) {
      setError('Voice recording is not supported in this browser');
      return;
    }

    if (isRecording) {
      return;
    }

    setError(null);
    audioChunksRef.current = [];

    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      // Determine best supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = () => {
        setError('Recording failed');
        setIsRecording(false);
        cleanupStream();
      };

      // Start recording with timeslice for chunked data
      mediaRecorder.start(100);
      setIsRecording(true);
    } catch (err) {
      let errorMessage = 'Failed to start recording';

      if (err instanceof DOMException) {
        switch (err.name) {
          case 'NotAllowedError':
            errorMessage = 'Microphone permission denied';
            break;
          case 'NotFoundError':
            errorMessage = 'No microphone found';
            break;
          case 'NotReadableError':
            errorMessage = 'Microphone is in use by another application';
            break;
        }
      }

      setError(errorMessage);
      cleanupStream();
    }
  }, [isSupported, isRecording, cleanupStream]);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    if (!mediaRecorderRef.current || !isRecording) {
      return null;
    }

    setIsRecording(false);

    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current!;

      mediaRecorder.onstop = async () => {
        // Create audio blob from chunks
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType,
        });

        cleanupStream();

        // Check if blob is too small (likely no audio)
        if (audioBlob.size < 1000) {
          setError('Recording too short');
          resolve(null);
          return;
        }

        // Transcribe if API service is available
        if (apiService) {
          setIsProcessing(true);
          setError(null);

          try {
            const response = await apiService.transcribeAudio(audioBlob);
            const text = response.text.trim();

            if (text) {
              onTranscription?.(text);
              resolve(text);
            } else {
              setError('Could not understand audio');
              resolve(null);
            }
          } catch (err) {
            const errorMessage = err instanceof ApiError
              ? err.message
              : 'Failed to transcribe audio';
            setError(errorMessage);
            resolve(null);
          } finally {
            setIsProcessing(false);
          }
        } else {
          setError('Widget not initialized');
          resolve(null);
        }
      };

      mediaRecorder.stop();
    });
  }, [isRecording, apiService, onTranscription, cleanupStream]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setError(null);
    cleanupStream();
  }, [isRecording, cleanupStream]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
    resetError,
    isSupported,
  };
}

export type { UseVoiceReturn };
